import { ed25519 } from "@noble/curves/ed25519.js";
import { randomBytes } from "@noble/hashes/utils.js";
import { unwrapKey, wrapKey } from "../crypto/aesKeyWrap.js";
import { deriveKey } from "../crypto/hkdf.js";
import {
  generateX25519KeyPair,
  publicKeyFromJwk,
  publicKeyToJwk,
  x25519SharedSecret,
  X25519KeyPair,
  X25519PublicJwk,
} from "../crypto/x25519.js";
import { SYNC_KDF_INFO, WRAP_KDF_INFO } from "../protocol/constants.js";

const SIGN_KDF_INFO = "trustii-wallet-sync-sign-v1";

export interface DeviceKeys {
  syncDeviceId: string;
  x25519: X25519KeyPair;
  signingPrivateKey: Uint8Array;
  signingPublicKey: Uint8Array;
}

export function generateAccountRootKey(): Uint8Array {
  return randomBytes(32);
}

export function deriveSyncKey(
  accountRootKey: Uint8Array,
  accountId: number | string,
): Uint8Array {
  return deriveKey(accountRootKey, String(accountId), SYNC_KDF_INFO);
}

export function generateDeviceKeys(syncDeviceId?: string): DeviceKeys {
  const x25519Keys = generateX25519KeyPair();
  const deviceId = syncDeviceId ?? crypto.randomUUID();
  const signingPrivateKey = deriveKey(
    x25519Keys.privateKey,
    deviceId,
    SIGN_KDF_INFO,
  );
  const signingPublicKey = ed25519.getPublicKey(signingPrivateKey);

  return {
    syncDeviceId: deviceId,
    x25519: x25519Keys,
    signingPrivateKey,
    signingPublicKey,
  };
}

export function deriveWrapKek(
  privateKey: Uint8Array,
  peerPublicKey: Uint8Array,
): Uint8Array {
  const shared = x25519SharedSecret(privateKey, peerPublicKey);
  return deriveKey(shared, new Uint8Array(), WRAP_KDF_INFO);
}

export function wrapSyncKeyForPeer(
  syncKey: Uint8Array,
  localPrivateKey: Uint8Array,
  peerPublicKey: Uint8Array,
): string {
  const kek = deriveWrapKek(localPrivateKey, peerPublicKey);
  const wrapped = wrapKey(syncKey, kek);
  return Buffer.from(wrapped).toString("base64");
}

export function unwrapSyncKeyFromPeer(
  wrappedSyncKeyB64: string,
  localPrivateKey: Uint8Array,
  peerPublicKey: Uint8Array,
): Uint8Array {
  const kek = deriveWrapKek(localPrivateKey, peerPublicKey);
  const wrapped = Buffer.from(wrappedSyncKeyB64, "base64");
  return unwrapKey(wrapped, kek);
}

export function selfWrapSyncKey(
  syncKey: Uint8Array,
  deviceKeys: DeviceKeys,
): string {
  return wrapSyncKeyForPeer(syncKey, deviceKeys.x25519.privateKey, deviceKeys.x25519.publicKey);
}

export function devicePublicKeyJwk(deviceKeys: DeviceKeys): X25519PublicJwk {
  return publicKeyToJwk(deviceKeys.x25519.publicKey);
}

export function unwrapSyncKeyFromJwk(
  wrappedSyncKeyB64: string,
  localPrivateKey: Uint8Array,
  peerJwk: X25519PublicJwk,
): Uint8Array {
  return unwrapSyncKeyFromPeer(
    wrappedSyncKeyB64,
    localPrivateKey,
    publicKeyFromJwk(peerJwk),
  );
}

export type { X25519PublicJwk } from "../crypto/x25519.js";
export { publicKeyFromJwk, publicKeyToJwk };
